import { Text } from '@/components/ui/text';
import { useAppearance } from '@/lib/design/appearance';
import { getDesignTokens } from '@/lib/design/theme';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import * as React from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

type PublicWebAction = {
  label: string;
  href: string;
  variant?: 'primary' | 'secondary';
};

type PublicWebPageProps = {
  eyebrow: string;
  title: string;
  body: string;
  actions: PublicWebAction[];
  children?: React.ReactNode;
};

const COMPACT_BREAKPOINT = 760;

export function PublicWebPage({ actions, body, children, eyebrow, title }: PublicWebPageProps) {
  const { push } = useRouter();
  const { width } = useWindowDimensions();
  const resolvedMode = useAppearance((state) => state.resolvedMode);
  const isCompact = width < COMPACT_BREAKPOINT;
  const tokens = React.useMemo(() => getDesignTokens(resolvedMode), [resolvedMode]);
  const styles = React.useMemo(() => createStyles(tokens, isCompact), [isCompact, tokens]);

  return (
    <LinearGradient
      colors={[
        tokens.colors.sceneGradientStart,
        tokens.colors.canvas,
        tokens.colors.sceneGradientEnd,
      ]}
      style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        <View style={styles.shell}>
          <View style={styles.nav}>
            <Pressable accessibilityRole="link" onPress={() => push('/')}>
              <Text style={styles.wordmark}>
                radious<Text style={styles.dot}>.</Text>
              </Text>
            </Pressable>
            <View style={styles.navLinks}>
              <Pressable
                accessibilityRole="link"
                style={styles.navLink}
                onPress={() => push('/events')}>
                <Text style={styles.navLinkText}>Events</Text>
              </Pressable>
              <Pressable
                accessibilityRole="link"
                style={styles.navLink}
                onPress={() => push('/sign-in')}>
                <Text style={styles.navLinkText}>Sign in</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.hero}>
            <View style={styles.kicker}>
              <Text style={styles.kickerText}>{eyebrow}</Text>
            </View>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.body}>{body}</Text>
            <View style={styles.actions}>
              {actions.map((action) => (
                <PublicWebButton
                  key={`${action.label}-${action.href}`}
                  action={action}
                  style={styles.actionButton}
                  styles={styles}
                />
              ))}
            </View>
          </View>

          {children ? <View style={styles.bodyGrid}>{children}</View> : null}
        </View>
      </ScrollView>
    </LinearGradient>
  );
}

function PublicWebButton({
  action,
  style,
  styles,
}: {
  action: PublicWebAction;
  style: StyleProp<ViewStyle>;
  styles: ReturnType<typeof createStyles>;
}) {
  const { push } = useRouter();
  const isExternal = action.href.startsWith('http') || action.href.startsWith('mailto:');
  const isPrimary = action.variant !== 'secondary';

  return (
    <Pressable
      accessibilityRole="link"
      style={({ pressed }) => [
        style,
        isPrimary ? styles.primaryButton : styles.secondaryButton,
        pressed ? styles.pressed : null,
      ]}
      onPress={() => {
        if (isExternal) {
          globalThis.location?.assign(action.href);
          return;
        }
        push(action.href as never);
      }}>
      <Text style={isPrimary ? styles.primaryButtonText : styles.secondaryButtonText}>
        {action.label}
      </Text>
    </Pressable>
  );
}

function createStyles(tokens: ReturnType<typeof getDesignTokens>, isCompact: boolean) {
  return StyleSheet.create({
    root: {
      flex: 1,
      minHeight: '100%',
    },
    scroll: {
      flex: 1,
    },
    content: {
      minHeight: '100%',
      paddingHorizontal: isCompact ? 20 : 36,
      paddingBottom: isCompact ? 34 : 64,
      paddingTop: isCompact ? 18 : 30,
    },
    shell: {
      alignSelf: 'center',
      gap: isCompact ? 32 : 46,
      maxWidth: 1080,
      width: '100%',
    },
    nav: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 18,
      justifyContent: 'space-between',
    },
    wordmark: {
      color: tokens.colors.text,
      fontFamily: tokens.typography.fonts.displayExtraBold,
      fontSize: isCompact ? 32 : 38,
      letterSpacing: -2,
      lineHeight: isCompact ? 38 : 44,
    },
    dot: {
      color: tokens.colors.accentStrong,
    },
    navLinks: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 8,
    },
    navLink: {
      borderRadius: tokens.radii.pill,
      paddingHorizontal: 13,
      paddingVertical: 9,
    },
    navLinkText: {
      color: tokens.colors.textSoft,
      fontFamily: tokens.typography.fonts.bodySemiBold,
      fontSize: 14,
    },
    hero: {
      gap: 18,
      maxWidth: 780,
      paddingTop: isCompact ? 8 : 34,
    },
    kicker: {
      alignSelf: 'flex-start',
      backgroundColor: tokens.colors.accentSoft,
      borderColor: tokens.colors.accentEdge,
      borderRadius: tokens.radii.pill,
      borderWidth: 1,
      paddingHorizontal: 13,
      paddingVertical: 8,
    },
    kickerText: {
      color: tokens.colors.accentPressed,
      fontFamily: tokens.typography.fonts.bodyBold,
      fontSize: 12,
      letterSpacing: 1.6,
      textTransform: 'uppercase',
    },
    title: {
      color: tokens.colors.text,
      fontFamily: tokens.typography.fonts.displayExtraBold,
      fontSize: isCompact ? 46 : 72,
      letterSpacing: isCompact ? -2.3 : -4,
      lineHeight: isCompact ? 49 : 72,
    },
    body: {
      color: tokens.colors.textSoft,
      fontFamily: tokens.typography.fonts.bodyMedium,
      fontSize: isCompact ? 18 : 21,
      lineHeight: isCompact ? 27 : 31,
    },
    actions: {
      flexDirection: isCompact ? 'column' : 'row',
      gap: 12,
      marginTop: 6,
    },
    actionButton: {
      alignItems: 'center',
      borderRadius: tokens.radii.pill,
      borderWidth: 1,
      paddingHorizontal: 24,
      paddingVertical: 15,
    },
    primaryButton: {
      backgroundColor: tokens.colors.accentStrong,
      borderColor: tokens.colors.accentEdge,
    },
    primaryButtonText: {
      color: tokens.colors.textInverse,
      fontFamily: tokens.typography.fonts.bodyBold,
      fontSize: 16,
    },
    secondaryButton: {
      backgroundColor: tokens.colors.surfaceOverlay,
      borderColor: tokens.colors.borderStrong,
    },
    secondaryButtonText: {
      color: tokens.colors.text,
      fontFamily: tokens.typography.fonts.bodyBold,
      fontSize: 16,
    },
    bodyGrid: {
      flexDirection: isCompact ? 'column' : 'row',
      flexWrap: 'wrap',
      gap: 14,
    },
    pressed: {
      opacity: 0.72,
    },
  });
}
